import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import HelpTooltip from "@/shared/components/help-tooltip";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useSettlementManagementQuery from "@/features/my-order/features/settlement-management/hooks/useSettlementManagementQuery";
import { addDays, endOfMonth, startOfMonth } from "date-fns";
import {
  formatDateToYMDEGeneral,
  formatDateToYMDGeneral,
} from "@/shared/utils";
import { UnoFeeStatistics } from "@/features/my-order/features/settlement-management/types";
import SettlementRemittanceStatusBadge from "@/features/my-order/components/SettlementRemittanceStatusBadge";

interface Props {
  statistics?: UnoFeeStatistics;
}

function SettlementManagementSummary({ statistics }: Props) {
  const { tTitle, tSentence, isKor } = useI18nHelper();
  const { query } = useSettlementManagementQuery();

  const currentDate = query.date ? new Date(query.date) : new Date();

  const month = currentDate.getMonth() + 1;
  const firstDay = startOfMonth(currentDate);
  const lastDay = endOfMonth(currentDate);
  const remittanceDue = addDays(lastDay, 7);

  const currentMonth = new Date().getMonth() + 1;
  const isLastMonth = month === currentMonth + 1;

  const {
    settlementStartDt,
    settlementEndDt,
    deadline,
    status,
    remittedOn,
    totalAmount,
  } = statistics ?? {};

  const hasBillingAmount = totalAmount && totalAmount > 0;

  const summaryList = [
    {
      title: "settlement_period",
      date: `${formatDateToYMDGeneral({
        date: settlementStartDt ?? firstDay,
        isKor: true,
        joinTxt: "-",
      })} ~ ${formatDateToYMDGeneral({
        date: settlementEndDt ?? lastDay,
        isKor: true,
        joinTxt: "-",
      })}`,
    },
    {
      title: "billing_date",
      date: formatDateToYMDGeneral({
        date: settlementEndDt ?? lastDay,
        isKor: true,
        joinTxt: "-",
      }),
    },
    {
      title: "remittance_due",
      date: `${formatDateToYMDGeneral({
        date: deadline ?? remittanceDue,
        isKor: true,
        joinTxt: "-",
      })}  ${isKor ? "23:59까지" : "23:59"}`,
    },
    {
      title: "remitted_date",
      date: `${
        remittedOn ? formatDateToYMDEGeneral(remittedOn ?? "", true, "-") : "-"
      }`,
    },
  ];

  return (
    <Flex
      flexDirection={"column"}
      gap={"8px"}
      padding={"12px 16px"}
      borderRadius={"8px"}
      background={!hasBillingAmount && !isLastMonth ? "#EFEFED" : "#F0F9FF"}
    >
      <Flex flexDirection={"column"} gap={"4px"}>
        <Flex gap={"4px"} alignItems={"center"}>
          <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
            {isKor
              ? `${month}월`
              : `${currentDate.toLocaleString("en-US", {
                  month: "long",
                })}`}{" "}
            {tTitle("settlement:billing_amount_vat")}
          </Text>
          <HelpTooltip
            label={tSentence("settlement:fee_tooltip.vat_description")}
          />
        </Flex>
        <Flex gap={"8px"} alignItems={"center"}>
          {!hasBillingAmount && !isLastMonth ? (
            <Text textStyle={"heading-md"} color={"#C0C0C0"}>
              {tTitle("settlement:no_billing_amount")}
            </Text>
          ) : (
            <>
              <Text textStyle={"heading-md"} color={"#1272EF"}>
                {totalAmount?.toLocaleString() ?? "-"}
                {isKor ? "원" : "₩"}
              </Text>
              <SettlementRemittanceStatusBadge status={status} />
            </>
          )}
        </Flex>
      </Flex>
      <Flex flexDirection={"column"}>
        {summaryList.map((item, index) => (
          <Flex gap={"2px"} key={index}>
            <Text textStyle={"body-sm-medium"} width={"80px"}>
              {tTitle(`settlement:${item.title}`)}
            </Text>
            <Text
              flex={1}
              textStyle={"body-sm-medium"}
              color={"#363636"}
              whiteSpace={"pre"}
            >
              {item.title !== "settlement_period" &&
              !hasBillingAmount &&
              !isLastMonth
                ? "-"
                : item.date}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

export default SettlementManagementSummary;
